// A failed external CSS download must not leave an interactive, unstyled page.
// Development injects this same marker via style-loader; production uses <link>.
export const appStylesReady = () => getComputedStyle(document.documentElement)
  .getPropertyValue('--a2-app-styles-ready').trim() === '1';

const waitForStyles = (ms) => new Promise(resolve => {
  const started = Date.now();
  const check = () => {
    if (appStylesReady()) return resolve(true);
    if (Date.now() - started >= ms) return resolve(false);
    setTimeout(check, 50);
  };
  check();
});

function retryStylesheet(href, attempt) {
  const url = new URL(href, window.location.origin);
  if (url.origin !== window.location.origin || !url.pathname.startsWith('/static/css/')) return;
  url.searchParams.set('style-retry', String(attempt));
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url.href;
  link.dataset.appStyleRetry = String(attempt);
  document.head.appendChild(link);
}

export async function ensureAppStyles() {
  if (appStylesReady() || await waitForStyles(250)) return true;
  let href = [...document.querySelectorAll('link[rel="stylesheet"]')]
    .map(link => link.href).find(url => new URL(url).origin === location.origin && /\/static\/css\/main\./.test(url));
  // The manifest also recovers stale HTML referring to a retired stylesheet hash.
  for (let attempt = 1; attempt <= 2; attempt++) {
    if (href) retryStylesheet(href, attempt);
    if (await waitForStyles(1500)) return true;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 1500);
    try {
      const response = await fetch('/asset-manifest.json', { cache: 'no-store', signal: controller.signal });
      if (response.ok) {
        const manifest = await response.json();
        if (manifest.files?.['main.css']) href = manifest.files['main.css'];
      }
    } catch { /* bounded retry, not a page-reload loop */ }
    finally { clearTimeout(timeout); }
  }
  if (href) retryStylesheet(href, 3);
  return waitForStyles(1500);
}

export function showStyleLoading(root, retry) {
  root.replaceChildren();
  const box = document.createElement('div');
  box.dataset.testid = retry ? 'page-style-error' : 'page-style-loading';
  box.setAttribute('role', 'status');
  box.style.cssText = 'padding:48px 24px;max-width:520px;margin:auto;text-align:center;font:16px sans-serif;line-height:1.5';
  box.textContent = retry ? 'The page styles could not load. Please try again.' : 'Loading…';
  if (retry) {
    const button = document.createElement('button');
    button.dataset.testid = 'page-style-retry';
    button.textContent = 'Retry';
    button.style.cssText = 'display:block;margin:20px auto;padding:12px 24px;cursor:pointer';
    button.onclick = retry;
    box.appendChild(button);
  }
  root.appendChild(box);
}